import { useCallback, useEffect, useRef, useState } from "react";
import { searchByDba } from "../../../api";

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 3;

/**
 * DBA typeahead search shared by the create-ticket modal and the ticket filter
 * drawer.
 *
 * Responses are sequenced: only the newest search may write to state, so a slow
 * response for "ac" can no longer land after "acme" and repaint the list with
 * results for a prefix the user has already typed past.
 *
 * searchDba() returns a promise that settles when the search settles, because
 * form/select drives its own results spinner off the returned value
 * (`Promise.resolve(onSearchChange(term)).finally(...)`). Returning a bare
 * undefined would switch that spinner off immediately.
 *
 * debounceMs is a parameter because the two consumers differ: form/select
 * already debounces internally (debounceDelay = 250), while form/tag-input
 * calls onSearchChange on every keystroke. Pass 0 when the consumer debounces
 * for you, so the two delays don't stack.
 */
export function useDbaSearch({ debounceMs = SEARCH_DEBOUNCE_MS } = {}) {
  const [dbaOptions, setDbaOptions] = useState([]);
  const [dbaLoading, setDbaLoading] = useState(false);
  const debounceTimeoutRef = useRef(null);
  const pendingResolveRef = useRef(null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  // Clear the pending timer and resolve its promise, so a consumer awaiting the
  // superseded search is never left hanging.
  const settlePending = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    if (pendingResolveRef.current) {
      pendingResolveRef.current();
      pendingResolveRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      settlePending();
    };
  }, [settlePending]);

  const resetDbaOptions = useCallback(() => {
    settlePending();
    // Bumping the id invalidates anything already in flight.
    requestIdRef.current += 1;
    setDbaOptions([]);
    setDbaLoading(false);
  }, [settlePending]);

  const searchDba = useCallback(
    (searchText) => {
      const term = (searchText || "").trim();

      // The endpoint validates min:3 — don't spend a request to be told so.
      if (term.length < MIN_SEARCH_LENGTH) {
        resetDbaOptions();
        return Promise.resolve();
      }

      settlePending();
      setDbaLoading(true);

      return new Promise((resolve) => {
        pendingResolveRef.current = resolve;

        debounceTimeoutRef.current = setTimeout(() => {
          const requestId = (requestIdRef.current += 1);
          const isCurrent = () =>
            mountedRef.current && requestIdRef.current === requestId;

          searchByDba({ dba: term })
            .then((response) => {
              if (isCurrent()) {
                setDbaOptions(response && response.status ? response.data : []);
                setDbaLoading(false);
              }
            })
            .catch((error) => {
              console.error("Error searching DBAs:", error);
              if (isCurrent()) {
                setDbaOptions([]);
                setDbaLoading(false);
              }
            })
            .finally(() => {
              if (pendingResolveRef.current === resolve) {
                pendingResolveRef.current = null;
              }
              resolve();
            });
        }, debounceMs);
      });
    },
    [debounceMs, resetDbaOptions, settlePending]
  );

  return { dbaOptions, dbaLoading, searchDba, resetDbaOptions };
}
